import { useCallback, useState } from "react";
import {
  Backdrop,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { RFP_COPY } from "@platform/feature-flags";
import { useSignedInUser } from "@platform/user/client";
import { useNavigate } from "react-router";

import { ROUTE_NAMES } from "../../constants";
import { useCopyRfp } from "../../api/rfp";

import type { ICellRendererParams } from "ag-grid-community";

export const ActionsCell: React.FC<ICellRendererParams> = ({ data }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { isAdminOrConsultant } = useSignedInUser();
  const rfpCopyEnabled = useFeatureIsOn(RFP_COPY);
  const navigate = useNavigate();
  const { mutate: copyRfp, isPending } = useCopyRfp();

  const showCopy = rfpCopyEnabled && isAdminOrConsultant;

  const handleOpen = useCallback(
    (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget),
    [],
  );

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleCopy = useCallback(() => {
    handleClose();
    const rfpUuid = data?.uuid as string | undefined;
    if (!rfpUuid) return;
    copyRfp(rfpUuid, {
      onSuccess: (result) => {
        navigate(`/${ROUTE_NAMES.RFPS}/${result.rfpId}`);
      },
    });
  }, [data, copyRfp, handleClose, navigate]);

  if (!showCopy) return null;

  return (
    <>
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.modal + 1 })}
        open={isPending}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <IconButton
        size="small"
        onClick={handleOpen}
        disabled={isPending}
        aria-label="RFP actions"
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={handleCopy} disabled={isPending}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy RFP</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
